import { useEffect, useMemo, useState } from 'react';

import { Declaration, DeclarationDocumentsEnum, DeclarationHighValueGroupType, EnvironmentCountries } from '@e-origin/shared';

import { DeclarationDocumentList, DocumentChangeProps } from '@components';
import { useUpdateDeclarationDocument } from '@hooks/declaration';
import { useSettings } from '@hooks/useSettings';
import { isHighValueDeclaration } from '@utils';

interface DeclarationViewDocumentsProps {
  editDisabled: boolean;
  declaration: Declaration;
}

export const DeclarationViewDocuments: React.FC<DeclarationViewDocumentsProps> = ({ editDisabled, declaration }) => {
  const { data: settings } = useSettings();
  const declarationGroup = declaration?.generalInfo?.group as DeclarationHighValueGroupType;
  const mutateDeclarationDocumentMutation = useUpdateDeclarationDocument();
  const [isHighValue, setIsHighValue] = useState<boolean>(isHighValueDeclaration(declarationGroup));

  useEffect(() => {
    setIsHighValue(isHighValueDeclaration(declarationGroup));
  }, [declarationGroup]);

  const handleSavingDocument = (data: DocumentChangeProps) => {
    mutateDeclarationDocumentMutation.mutate({ doc: data, lrn: declaration.messageInfo.LRN, sequence: declaration.goodShipmentGlobal?.sequence });
  };

  const excludeDocumentTypes = useMemo(() => {
    if (!isHighValue) {
      return [
        DeclarationDocumentsEnum.GUARANTEE_DOCUMENTS,
        DeclarationDocumentsEnum.WAREHOUSE_DOCUMENT,
        DeclarationDocumentsEnum.AUTHORISATION_DOCUMENTS,
        DeclarationDocumentsEnum.ADDITIONS_AND_DEDUCTIONS,
      ];
    }

    if (declarationGroup === DeclarationHighValueGroupType.H2) {
      const documentTypes = [DeclarationDocumentsEnum.ADDITIONS_AND_DEDUCTIONS, DeclarationDocumentsEnum.ADDITIONAL_FISCAL_REFERENCE];
      if (settings.country === EnvironmentCountries.NL) {
        documentTypes.push(DeclarationDocumentsEnum.GUARANTEE_DOCUMENTS);
      }
      return documentTypes;
    }

    return undefined;
  }, [isHighValue, declarationGroup, settings.country]);

  return (
    <DeclarationDocumentList
      documents={declaration.documents}
      editDisabled={editDisabled}
      onDocumentChange={handleSavingDocument}
      isHighValue={isHighValue}
      excludeDocumentTypes={excludeDocumentTypes}
      defaultHeaderValue={true}
      headerDisabled={false}
      declaration={declaration}
    />
  );
};
